import {
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  RefreshCwIcon,
  Loader2Icon,
  AlertTriangleIcon,
  KeyRoundIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import type { VMInstanceDetails } from '@nebula/shared/services/instancesApi'
// Espelha `vm_os_service.DEFAULT_WINDOWS_USER`; a segunda tela que precisar do default
// não deve redigitar a string.
import { DEFAULT_WINDOWS_USER as WINDOWS_DEFAULT_USER } from '@nebula/shared/utils'
import { ehWindowsParaApresentacao } from '@nebula/shared/utils/vmOsFamily'

/**
 * Credenciais de acesso da VM — e, quando não há credencial a mostrar, o caminho real.
 *
 * Extraído da `InstanceDetailPage` na issue #729. O bloco antigo aparecia em qualquer VM,
 * inclusive Windows, exibindo o par que o clone gerou para o cloud-init. Em Windows esse
 * par nunca foi aplicado: depende do Cloudbase-Init, e o usuário gravado (`admin`/
 * `cladmin`) sequer existe na máquina. O cliente copiava, abria o RDP e não entrava.
 *
 * Quem decide o que aparece é o BACKEND, via `supports_cloudinit_credentials` — não um
 * `!isWindows` no JSX. A régua antiga do front (`proxmox_details.os_type`) lê dado vivo do
 * PVE: quando a consulta falha o campo some, o teste vira `false` e a VM Windows volta a
 * exibir a senha exatamente no momento em que o sistema está degradado.
 */

type Props = {
  instance: VMInstanceDetails
  /** Windows resolvido pela família do template (com fallback para o PVE na página). */
  isWindows: boolean
  /** Devolve a promise da mutation: o formulário só se fecha quando ela RESOLVE. */
  onReset: (data: { new_password: string; username?: string }) => Promise<unknown>
  resetPending: boolean
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className="ml-1.5 inline-flex items-center text-slate-400 transition-colors hover:text-slate-600"
      title="Copiar"
    >
      <CopyIcon size={13} className={copied ? 'text-emerald-500' : ''} />
    </button>
  )
}

export default function InstanceCredentialsCard({
  instance,
  isWindows,
  onReset,
  resetPending,
}: Props) {
  const [showPassword, setShowPassword] = useState(false)
  const [showResetForm, setShowResetForm] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  // A credencial só é anunciada quando o backend afirma que ela descreve o login real.
  // O `&&` com os campos cobre a VM cujo par simplesmente não existe (criação antiga).
  //
  // O caso `undefined` é separado do `false` de propósito. Os deploys deste repo são
  // INDEPENDENTES (Cloudflare Pages para o front, Fly para a API), então existe uma
  // janela real — e um rollback, e resposta em cache — em que esta tela nova fala com a
  // API antiga: sem o campo, e ainda devolvendo `initial_user`/`initial_password` de
  // cloud-init para VM Windows. Ler isso como "não é false, logo pode exibir" faria o
  // vazamento que a issue fecha reaparecer exatamente durante o deploy.
  //
  // Sem `os_family` não existe régua do servidor — seja porque a API é antiga (`undefined`)
  // ou porque ela não soube a família (`null`). Nos dois casos a única pista é o PVE, e ela
  // serve para ESCONDER: nada que o backend tenha comprovado é escondido por isto, porque
  // sem família não há prova possível.
  const credenciaisEfetivas = instance.os_family
    ? instance.supports_cloudinit_credentials !== false
    : !ehWindowsParaApresentacao(instance)
  const exibeCredenciais =
    credenciaisEfetivas && !!instance.initial_user && !!instance.initial_password

  // O `initial_user` só pré-preenche o formulário quando o backend o declara efetivo.
  // Hoje ele já vem nulo sem prova, então isto não muda nada — é a defesa em
  // profundidade que impede o campo de sugerir a conta ERRADA (o `admin` do cloud-init
  // Linux, que não existe no Windows) caso um caminho novo passe a devolver o par sem
  // prova. Sugerir conta inexistente é o defeito da issue mudando de lugar.
  const [username, setUsername] = useState(
    isWindows ? (credenciaisEfetivas && instance.initial_user) || WINDOWS_DEFAULT_USER : '',
  )

  const submeter = () => {
    if (newPassword.length < 6) {
      toast.error('Mínimo 6 caracteres')
      return
    }
    if (isWindows && !username.trim()) {
      toast.error('Informe o usuário do Windows')
      return
    }
    // Fechar só depois que a mutation resolve. Fechando antes, o erro mais provável
    // deste fluxo — o 400 de "a VM precisa estar ligada", numa VM Windows desligada —
    // chegava como toast sobre um formulário já fechado, e o cliente tinha de reabrir e
    // redigitar tudo. De quebra, é o que torna o `resetPending` ("Redefinindo…")
    // alcançável: antes o formulário sumia antes de a task sequer ser enfileirada.
    onReset({
      new_password: newPassword,
      username: isWindows ? username.trim() : undefined,
    })
      .then(() => {
        setNewPassword('')
        setShowResetForm(false)
      })
      .catch(() => {
        // O toast de erro é do chamador; aqui só preservamos o que foi digitado.
      })
  }

  const formularioDeReset = (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <div className="grid max-w-2xl grid-cols-1 gap-3 md:grid-cols-2">
        {isWindows && (
          <div>
            <label htmlFor="reset-username" className="mb-1 block text-xs text-slate-500">
              Usuário do Windows <span className="text-red-500">*</span>
            </label>
            <input
              id="reset-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={WINDOWS_DEFAULT_USER}
              maxLength={64}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-900/10"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              A conta que existe dentro da máquina. Se ela foi renomeada, informe o nome atual.
            </p>
          </div>
        )}
        <div>
          <label htmlFor="reset-password" className="mb-1 block text-xs text-slate-500">
            Nova Senha <span className="text-red-500">*</span>
          </label>
          <input
            id="reset-password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Mínimo 6 caracteres"
            minLength={6}
            maxLength={128}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-slate-300 focus:ring-2 focus:ring-slate-900/10"
          />
        </div>
      </div>

      {/* O aviso segue o que de fato acontece em cada caminho: no Windows a troca é feita
          pelo guest agent e a VM não é reiniciada; em Linux o reboot ainda é o plano B. */}
      <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200/60 bg-amber-50 p-3">
        <AlertTriangleIcon className="mt-0.5 size-4 shrink-0 text-amber-600" />
        <p className="text-xs text-amber-700">
          {isWindows
            ? 'A senha é aplicada pelo agente convidado, sem reiniciar a VM. A máquina precisa estar ligada e com o QEMU Guest Agent em execução.'
            : 'Se o agente convidado não responder, a VM será reiniciada para aplicar as novas credenciais.'}
        </p>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          onClick={() => {
            setShowResetForm(false)
            setNewPassword('')
          }}
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          onClick={submeter}
          disabled={resetPending || newPassword.length < 6}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
        >
          {resetPending ? (
            <>
              <Loader2Icon className="size-4 animate-spin" /> Redefinindo...
            </>
          ) : (
            'Confirmar'
          )}
        </button>
      </div>
    </div>
  )

  if (exibeCredenciais) {
    return (
      <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Credenciais
          </h4>
          {!showResetForm && (
            <button
              onClick={() => setShowResetForm(true)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
            >
              <RefreshCwIcon className="size-3.5" /> Redefinir Senha
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-slate-400">Usuário</label>
            <div className="flex items-center gap-2">
              <code className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5 font-mono text-sm text-slate-700">
                {instance.initial_user}
              </code>
              <CopyButton text={instance.initial_user!} />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-400">Senha</label>
            <div className="flex items-center gap-2">
              <code className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5 font-mono text-sm text-slate-700">
                {showPassword ? instance.initial_password : '••••••••••••'}
              </code>
              <button
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                className="text-slate-400 transition-colors hover:text-slate-600"
              >
                {showPassword ? (
                  <EyeOffIcon className="size-3.5" />
                ) : (
                  <EyeIcon className="size-3.5" />
                )}
              </button>
              <CopyButton text={instance.initial_password!} />
            </div>
          </div>
        </div>

        {showResetForm && formularioDeReset}
      </div>
    )
  }

  // Windows sem senha comprovada: em vez do par inútil, o caminho que funciona.
  //
  // A régua de APRESENTAÇÃO entra aqui (e só aqui): este bloco é instrução de acesso —
  // Console, RDP, "Definir Senha" —, não uma afirmação sobre o que o servidor fará. Sem
  // ela, na janela de deploy com a API antiga (`os_family` ausente, logo `isWindows`
  // falso) a VM Windows caía no `return null` de baixo: o par sumia — correto — e junto
  // sumia a única instrução de como entrar na máquina, deixando a tela vazia.
  if (isWindows || ehWindowsParaApresentacao(instance)) {
    return (
      <div className="rounded-2xl border border-slate-200/60 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
            Acesso ao Windows
          </h4>
          {/* A ação exige `isWindows` — a régua do BACKEND, não a pista do PVE que pode
              ter montado este bloco. Sem ela o formulário não pede a conta, o payload vai
              sem `username`, e o servidor (que também não sabe que é Windows) trata a VM
              como Linux: reset no usuário de cloud-init e reboot de fallback — reiniciar a
              VM Windows do cliente sem trocar senha nenhuma, que é o defeito da #729
              disparado pelo próprio botão que deveria consertá-lo.
              Vale para os dois casos em que a régua falta: a API antiga da janela de
              deploy e a VM cuja família o backend não resolveu (`os_family: null`).
              As instruções de acesso continuam visíveis; só a ação espera. */}
          {!showResetForm && isWindows && (
            <button
              onClick={() => setShowResetForm(true)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-700"
            >
              <KeyRoundIcon className="size-3.5" /> Definir Senha
            </button>
          )}
        </div>
        <p className="text-sm text-slate-600">
          A senha do{' '}
          <code className="rounded border border-slate-100 bg-slate-50 px-1.5 py-0.5 font-mono text-[13px]">
            {WINDOWS_DEFAULT_USER}
          </code>{' '}
          é definida na primeira inicialização do Windows, dentro da máquina — o portal não a
          conhece.
        </p>
        <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-slate-500">
          <li>
            Para o primeiro acesso, use o{' '}
            <strong className="font-medium text-slate-600">Console</strong> acima.
          </li>
          <li>
            Com a rede ativa, use a{' '}
            <strong className="font-medium text-slate-600">Área de Trabalho Remota (RDP)</strong>.
          </li>
          <li>
            Para trocar a senha depois, faça isso dentro do Windows — ou use{' '}
            <strong className="font-medium text-slate-600">Definir Senha</strong> aqui.
          </li>
        </ul>

        {showResetForm && formularioDeReset}
      </div>
    )
  }

  return null
}
